import React, { useEffect, useState } from 'react';
import { useParams, useNavigate as useKeplerNavigate } from 'react-router';
import { useAppActions, useAppState } from '../../../overmind';
import { FetchType } from '../../../enums';
import { PagePath } from '../../../navigation/navigation.enums';
import { useForm } from 'react-hook-form';
import { Button, FieldController, PageHeader, RequiredValidator, TextArea, TextField } from '@keplerco/core';
import { ContextDto, UpsertFreeTextContextRequest } from '../../../overmind/actions/ai/ai-contexts';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';

export function FreeTextContextPage(): JSX.Element {
  const { contextId } = useParams();
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const { control, handleSubmit, reset } = useForm();

  const [context, setContext] = useState<ContextDto>();

  // Load context data if editing an existing context
  useEffect(() => {
    async function getContext() {
      if (contextId === 'freetext' || !contextId) return;

      actions.startLoader({ path: PagePath.aiManagementContext, type: FetchType.PageFetching });

      const response = await actions.getContextById(contextId);
      setContext(response);
      reset({
        label: response?.label,
        text: response?.text,
      });

      actions.stopLoader(PagePath.aiManagementContext);
    }

    getContext();
  }, [contextId]);

  return (
    <PageLoaderLayer path={PagePath.aiManagementContext}>
      <div className="wrapper stack">
        <PageHeader
          breadcrumbs={[
            { name: 'AI management', url: `${PagePath.aiManagementBase}${PagePath.aiManagementAgents.replace(':companySlug', companyVariables.slug!)}` },
            { name: 'Contexts', url: `${PagePath.aiManagementBase}${PagePath.aiManagementContexts.replace(':companySlug', companyVariables.slug!)}` },
            { name: contextId === 'freetext' ? 'Create free text context' : `Edit ${context?.label ?? 'context'}`, url: '' },
          ]}
          title={contextId === 'freetext' ? 'Create free text context' : `Edit ${context?.label ?? 'context'}`}
          subtitle="Create or edit a free text context for your AI agents"
          divider
        />

        <form
          id="freetextContext"
          style={{ marginTop: 24, width: '100%' }}
          onSubmit={handleSubmit(async fieldValues => {
            if (!companyVariables.slug) return;

            actions.startLoader({ path: PagePath.aiManagementContext, type: FetchType.Sending });

            const request: UpsertFreeTextContextRequest = {
              label: fieldValues.label,
              text: fieldValues.text,
              id: contextId === 'freetext' ? undefined : contextId,
            };
            await actions.upsertFreeTextContext(request);
            keplerNavigate(`${PagePath.aiManagementBase}${PagePath.aiManagementContexts.replace(':companySlug', companyVariables.slug)}`);

            actions.stopLoader(PagePath.aiManagementContext);
          })}
        >
          <FieldController
            control={control}
            name="label"
            rules={new RequiredValidator('Enter a label')}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="text"
                label="Label"
                responsive
                validation={{
                  dirty: fieldState.isDirty || !!fieldState.error,
                  invalid: fieldState.invalid || !!fieldState.error,
                  message: fieldState.error?.message ?? 'Enter a label',
                }}
              />
            )}
          />

          <FieldController
            control={control}
            name="text"
            rules={new RequiredValidator('Enter text')}
            render={({ field, fieldState }) => (
              <TextArea
                {...field}
                label="Text"
                responsive
                validation={{
                  dirty: fieldState.isDirty || !!fieldState.error,
                  invalid: fieldState.invalid || !!fieldState.error,
                  message: fieldState.error?.message ?? 'Enter text',
                }}
              />
            )}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="button">Save</Button>
          </div>
        </form>
      </div>
    </PageLoaderLayer>
  );
}
